import { Money, dec } from "@despejo/domain";

/**
 * IVA — régimen general, monthly (plan §15, docs/tax/IVA_GENERAL.md).
 * Pure: (inputs, parameters) -> result + trace. Never guesses: anything it
 * cannot classify becomes an exception that blocks READY_TO_FILE.
 *
 * Sources: Ley 6826 arts. 10-11 (rates), 22 (proportionality), 27 (nil
 * returns), 28 (carry-forward), 29 (card retentions); Reglamento 41779-H
 * arts. 29.2.a (credit needs timely acceptance), 34-36 (prorrata).
 */

export const ENGINE_VERSION = "iva-general@1.0.0";

export type SaleCategory = "TAXED" | "EXEMPT" | "EXPORT" | "NON_SUBJECT";
/** How a purchase is used: directly for taxed sales, for exempt sales, or both (prorrata). */
export type PurchaseUse = "TAXED" | "EXEMPT" | "MIXED";

export interface SaleLine {
  ref: string;
  category: SaleCategory;
  /** IVA rate as a decimal string ("0.13", "0.04", ...); required for TAXED. */
  rate: string | null;
  base: string;
  iva: string;
  /** Credit note lines carry negative base/iva. */
}

export interface PurchaseLine {
  ref: string;
  rate: string | null;
  base: string;
  iva: string;
  use: PurchaseUse;
  /** Receiver acceptance recorded within the legal window (Reglamento art. 29.2.a). */
  accepted: boolean;
  /**
   * Not accepted yet, but the acceptance deadline (this date) has not passed at
   * calculation time: the credit counts provisionally, with an ACCEPTANCE_PENDING
   * exception. Once the deadline passes the caller sends null and the credit is denied.
   */
  acceptancePendingUntil?: string | null;
  hasElectronicReceipt: boolean;
  businessUse: "BUSINESS" | "PERSONAL" | "MIXED";
  businessPct: string | null;
  /** Rate of the sales this purchase serves, when reduced (art. 34.2 cap). */
  servesReducedRate: string | null;
}

export interface IvaInput {
  period: string;
  sales: SaleLine[];
  purchases: PurchaseLine[];
  /** Provisional proportion from the previous year (art. 35.1), percent with two decimals; null = compute from this period. */
  provisionalProportionPct: string | null;
  cardRetentions: string;
  priorCreditBalance: string;
}

export interface IvaException {
  code: "UNKNOWN_IVA_TREATMENT" | "UNACCEPTED_RECEIPT" | "ACCEPTANCE_PENDING" | "NO_ELECTRONIC_RECEIPT" | "MISSING_RATE" | "PRORRATA_UNKNOWN";
  ref: string;
  message: string;
}

export interface IvaResult {
  engine: string;
  period: string;
  debitByRate: Record<string, { base: string; iva: string }>;
  salesExempt: string;
  salesExport: string;
  salesNonSubject: string;
  totalDebit: string;
  purchasesByRate: Record<string, { base: string; iva: string }>;
  creditFull: string;
  creditProrrata: string;
  creditNotAllowed: string;
  proportionPct: string | null;
  totalCredit: string;
  cardRetentions: string;
  priorCreditBalance: string;
  payable: string;
  creditCarryForward: string;
  isNil: boolean;
  exceptions: IvaException[];
  trace: { bucket: string; ref: string; amount: string; note?: string }[];
}

const r2 = (m: Money) => m.round(2).toFixed(2);

export function computeIva(input: IvaInput): IvaResult {
  const trace: IvaResult["trace"] = [];
  const exceptions: IvaException[] = [];
  const debitByRate = new Map<string, { base: Money; iva: Money }>();
  const purchasesByRate = new Map<string, { base: Money; iva: Money }>();
  let exempt = Money.zero();
  let exportSales = Money.zero();
  let nonSubject = Money.zero();

  // ------------------------------------------------------------- sales
  for (const s of input.sales) {
    const base = Money.of(s.base);
    const iva = Money.of(s.iva);
    if (s.category === "TAXED") {
      if (!s.rate) {
        exceptions.push({ code: "MISSING_RATE", ref: s.ref, message: "Venta gravada sin tarifa (revise el CABYS)" });
        continue;
      }
      const cur = debitByRate.get(s.rate) ?? { base: Money.zero(), iva: Money.zero() };
      debitByRate.set(s.rate, { base: cur.base.add(base), iva: cur.iva.add(iva) });
      trace.push({ bucket: `debit:${s.rate}`, ref: s.ref, amount: iva.toString() });
    } else if (s.category === "EXEMPT") {
      exempt = exempt.add(base);
      trace.push({ bucket: "sales:exempt", ref: s.ref, amount: base.toString() });
    } else if (s.category === "EXPORT") {
      exportSales = exportSales.add(base);
      trace.push({ bucket: "sales:export", ref: s.ref, amount: base.toString() });
    } else {
      nonSubject = nonSubject.add(base);
      trace.push({ bucket: "sales:non_subject", ref: s.ref, amount: base.toString() });
    }
  }
  const taxedBase = Money.sum([...debitByRate.values()].map((v) => v.base));
  const totalDebit = Money.sum([...debitByRate.values()].map((v) => v.iva));

  // ------------------------------------------------------- proportion
  // Art. 34.1: operations with full credit (taxed + exports) / total operations, two decimals.
  const withCredit = taxedBase.add(exportSales);
  const totalOps = withCredit.add(exempt);
  let proportion: string | null = input.provisionalProportionPct;
  if (proportion === null) {
    proportion = totalOps.isZero() ? null : withCredit.dividedBy(totalOps.amount.toString()).times(100).round(2, "DOWN").toFixed(2);
  }

  // --------------------------------------------------------- purchases
  let creditFull = Money.zero();
  let creditPro = Money.zero();
  let notAllowed = Money.zero();
  for (const p of input.purchases) {
    const iva = Money.of(p.iva);
    const base = Money.of(p.base);
    if (p.rate) {
      const cur = purchasesByRate.get(p.rate) ?? { base: Money.zero(), iva: Money.zero() };
      purchasesByRate.set(p.rate, { base: cur.base.add(base), iva: cur.iva.add(iva) });
    }
    if (iva.isZero()) continue;
    const deny = (why: string, code?: IvaException["code"]) => {
      notAllowed = notAllowed.add(iva);
      trace.push({ bucket: "credit:not_allowed", ref: p.ref, amount: iva.toString(), note: why });
      if (code) exceptions.push({ code, ref: p.ref, message: why });
    };
    if (!p.hasElectronicReceipt) {
      deny("Sin comprobante electrónico: no genera crédito", "NO_ELECTRONIC_RECEIPT");
      continue;
    }
    if (!p.accepted) {
      if (!p.acceptancePendingUntil) {
        deny("Comprobante sin aceptación en plazo (Reglamento art. 29.2.a)", "UNACCEPTED_RECEIPT");
        continue;
      }
      exceptions.push({ code: "ACCEPTANCE_PENDING", ref: p.ref, message: `Pendiente de aceptar a más tardar el ${p.acceptancePendingUntil}: si no se acepta en plazo, este IVA deja de ser acreditable (Reglamento art. 29.2.a)` });
    }
    if (p.businessUse === "PERSONAL") {
      deny("Compra personal");
      continue;
    }
    let eligible = iva;
    if (p.businessUse === "MIXED") {
      const pct = p.businessPct ?? "0";
      eligible = iva.times(dec(pct).dividedBy(100).toString());
      notAllowed = notAllowed.add(iva.sub(eligible));
      trace.push({ bucket: "credit:not_allowed", ref: p.ref, amount: iva.sub(eligible).toString(), note: `Uso personal ${dec(100).minus(pct).toString()}%` });
    }
    if (p.servesReducedRate && p.rate && dec(p.servesReducedRate).lt(p.rate)) {
      // Art. 34.2: credit capped at the reduced rate of the output operation.
      const capped = base.times(p.servesReducedRate);
      const scaledCap = p.businessUse === "MIXED" ? capped.times(dec(p.businessPct ?? "0").dividedBy(100).toString()) : capped;
      if (scaledCap.cmp(eligible) < 0) {
        notAllowed = notAllowed.add(eligible.sub(scaledCap));
        trace.push({ bucket: "credit:not_allowed", ref: p.ref, amount: eligible.sub(scaledCap).toString(), note: `Tope de tarifa reducida ${p.servesReducedRate}` });
        eligible = scaledCap;
      }
    }
    if (p.use === "TAXED") {
      creditFull = creditFull.add(eligible);
      trace.push({ bucket: "credit:full", ref: p.ref, amount: eligible.toString() });
    } else if (p.use === "EXEMPT") {
      notAllowed = notAllowed.add(eligible);
      trace.push({ bucket: "credit:not_allowed", ref: p.ref, amount: eligible.toString(), note: "Destinada a operaciones exentas (art. 34.3: costo)" });
    } else {
      if (proportion === null) {
        exceptions.push({ code: "PRORRATA_UNKNOWN", ref: p.ref, message: "No hay proporción para la prorrata" });
        continue;
      }
      const part = eligible.times(dec(proportion).dividedBy(100).toString());
      creditPro = creditPro.add(part);
      notAllowed = notAllowed.add(eligible.sub(part));
      trace.push({ bucket: "credit:prorrata", ref: p.ref, amount: part.toString(), note: `${proportion}%` });
      const denied = eligible.sub(part);
      if (!denied.isZero()) trace.push({ bucket: "credit:not_allowed", ref: p.ref, amount: denied.toString(), note: `Parte no acreditable por prorrata (${proportion}% acreditable; art. 34.3: costo)` });
    }
  }

  const totalCredit = creditFull.add(creditPro);
  const retentions = Money.of(input.cardRetentions);
  const prior = Money.of(input.priorCreditBalance);
  const balance = totalDebit.sub(totalCredit).sub(retentions).sub(prior);
  const payable = balance.isPositive() ? balance : Money.zero();
  const carry = balance.isNegative() ? balance.neg() : Money.zero();

  const rec = (m: Map<string, { base: Money; iva: Money }>) =>
    Object.fromEntries([...m.entries()].sort().map(([k, v]) => [k, { base: r2(v.base), iva: r2(v.iva) }]));

  return {
    engine: ENGINE_VERSION,
    period: input.period,
    debitByRate: rec(debitByRate),
    salesExempt: r2(exempt),
    salesExport: r2(exportSales),
    salesNonSubject: r2(nonSubject),
    totalDebit: r2(totalDebit),
    purchasesByRate: rec(purchasesByRate),
    creditFull: r2(creditFull),
    creditProrrata: r2(creditPro),
    creditNotAllowed: r2(notAllowed),
    proportionPct: proportion,
    totalCredit: r2(totalCredit),
    cardRetentions: r2(retentions),
    priorCreditBalance: r2(prior),
    payable: r2(payable),
    creditCarryForward: r2(carry),
    isNil: input.sales.length === 0 && input.purchases.length === 0,
    exceptions,
    trace,
  };
}
